import { STATUS_LABELS } from "@shared/status-labels";
import type {
  JobPipelineKanbanActions,
  JobPipelineKanbanData,
  KanbanApplication,
  KanbanColumn,
} from "@shared/views/job-pipeline/types";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Linkedin,
  Loader2,
  Mail,
  MoreHorizontal,
  Search,
  Users,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { StatusBadge } from "@/components/status-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getInterviewContextStatus, isSchedulingOverdue } from "@/lib/interview-utils";
import { ensureExternalUrl } from "@/lib/utils";

const EDGE_ZONE = 80;
const MAX_SCROLL_SPEED = 18;

type AiScreeningResult = {
  status: "pending" | "running" | "done" | "failed";
  verdict?: "interview" | "do_not_interview";
  level?: string;
};

// Map kanban interview-status labels (in PT-BR) → status-badge keys.
const interviewStatusToBadgeKey: Record<string, string> = {
  Agendar: "pending",
  Agendada: "pending",
  Realizada: "completed",
  Aprovado: "approved",
  Recusado: "rejected",
  "Sem feedback": "pending",
};

// Map offer status raw key → status-badge canonical key.
const offerStatusToBadgeKey: Record<string, string> = {
  waiting_approval: "pending_approval",
  approved: "approved",
  rejected: "rejected",
  sent: "sent",
  accepted: "accepted",
  declined: "declined",
  cancelled: "cancelled",
};

function initials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function CardAvatar({
  app,
  column,
  data,
}: {
  app: KanbanApplication;
  column: KanbanColumn;
  data: JobPipelineKanbanData;
}) {
  if (column.type === "stage") {
    const stageInterview = app.interviews
      ?.filter((i) => i.interviewStageId === column.stageId)
      .sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
      )[0];
    if (stageInterview) {
      if (stageInterview.interviewerGroupId) {
        const stageData = data.stageInterviewers?.find((si) => si.stageId === column.stageId);
        const group = stageData?.groups?.find((g) => g.id === stageInterview.interviewerGroupId);
        const memberNames = group?.members.map((m) => m.fullName).join(" e ") || "Grupo";
        return (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="w-7 h-7 rounded-full bg-[hsl(var(--forest-mist))] flex items-center justify-center flex-shrink-0 cursor-default"
                  data-testid={`icon-interviewer-group-${app.id}`}
                >
                  <Users className="w-3.5 h-3.5 text-[hsl(var(--forest-pulse))]" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Entrevistadores: {memberNames}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      const interviewer = stageInterview.interviewerId
        ? data.peopleById?.[stageInterview.interviewerId]
        : null;
      if (interviewer) {
        return (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="w-7 h-7 rounded-full bg-[hsl(var(--forest-mist))] flex items-center justify-center flex-shrink-0 cursor-default"
                  data-testid={`icon-interviewer-${app.id}`}
                >
                  <span className="text-xs font-medium text-[hsl(var(--forest-pulse))]">
                    {initials(interviewer.fullName)}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Entrevistador: {interviewer.fullName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
    }
  }
  if (app.owner) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="w-7 h-7 rounded-full bg-[hsl(var(--coral-mist))] flex items-center justify-center flex-shrink-0 cursor-default"
              data-testid={`icon-owner-${app.id}`}
            >
              <span className="text-xs font-medium text-[hsl(var(--comp-coral))]">
                {initials(app.owner.fullName)}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Responsável: {app.owner.fullName}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return null;
}

function CardScreening({ app }: { app: KanbanApplication }) {
  const sr = app.aiScreeningResult as AiScreeningResult | null;
  if (!sr) return null;
  if (sr.status === "pending" || sr.status === "running") {
    return (
      <div className="flex items-center gap-1 mt-1.5 text-[10px] text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" />
        Avaliando...
      </div>
    );
  }
  if (sr.status === "done") {
    return (
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <span
          className={`text-[11px] font-semibold ${
            sr.verdict === "interview"
              ? "text-[hsl(var(--forest-pulse))]"
              : "text-[hsl(var(--alert-red))]"
          }`}
        >
          {sr.verdict === "interview" ? "Entrevistar" : "Não entrevistar"}
        </span>
        {sr.level && (
          <span className="text-[11px] font-medium text-[hsl(var(--ink-muted))]">{sr.level}</span>
        )}
      </div>
    );
  }
  return null;
}

function CardColumnBadges({
  app,
  column,
  notifyHmDailyDigest,
}: {
  app: KanbanApplication;
  column: KanbanColumn;
  notifyHmDailyDigest: boolean;
}) {
  if (column.id === "rejected") {
    return app.rejectionFeedbackSentAt ? (
      <span className="text-[12px] font-semibold text-[hsl(var(--forest-pulse))]">Comunicado</span>
    ) : (
      <span className="text-[12px] font-semibold text-[hsl(var(--ink-muted))]">Não comunicado</span>
    );
  }
  if (column.id === "offer" || column.id === "hired") {
    const offerStatus = app.latestOfferStatus;
    if (!offerStatus) {
      return (
        <span className="text-[12px] font-semibold text-[hsl(var(--ink-muted))]">Sem oferta</span>
      );
    }
    const key = offerStatusToBadgeKey[offerStatus];
    if (!key) return null;
    return <StatusBadge status={key} className="text-[12px]" />;
  }
  if (column.id === "reference_checks") {
    const summary = app.referenceChecksSummary;
    if (!summary || summary.total === 0) {
      return (
        <span className="text-[12px] font-semibold text-[hsl(var(--ink-muted))]">
          Sem reference checks
        </span>
      );
    }
    if (summary.answered === 0) {
      return (
        <span className="text-[12px] font-semibold text-[hsl(var(--amber-state))]">
          Todos pendentes
        </span>
      );
    }
    if (summary.answered === summary.total) {
      return (
        <span className="text-[12px] font-semibold text-[hsl(var(--forest-pulse))] tabular-nums">
          {summary.answered} finalizados
        </span>
      );
    }
    return (
      <span className="text-[12px] font-semibold text-[hsl(var(--amber-state))] tabular-nums">
        {summary.answered}/{summary.total} finalizados
      </span>
    );
  }
  if (column.id === "dossier") {
    const decision = app.dossierDecision;
    if (!decision) {
      return (
        <span className="text-[12px] font-semibold text-[hsl(var(--amber-state))]">
          Aguardando decisão
        </span>
      );
    }
    return <StatusBadge status={decision} className="text-[12px]" />;
  }
  // default: per-stage interview badges
  const targetStageId = column.type === "stage" ? column.stageId : app.currentInterviewStageId;
  const stageInterviews =
    app.interviews
      ?.filter((i) => i.interviewStageId === targetStageId)
      .sort(
        (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
      ) ?? [];
  const multiple = stageInterviews.length > 1;
  return (
    <div className="flex flex-col gap-1">
      {stageInterviews.map((interview, idx) => {
        const label =
          ((interview as unknown as { contextStatus?: string }).contextStatus as string) ??
          getInterviewContextStatus(interview);
        const overdue = isSchedulingOverdue(interview, notifyHmDailyDigest);
        const badgeKey = interviewStatusToBadgeKey[label] ?? "pending";
        // Overdue = a coral nudge: needs attention now (DESIGN §10.9).
        const overdueOverride = overdue ? "text-[hsl(var(--comp-coral))]" : undefined;
        const rec = !interview.interviewerGroupId ? interview.recommendation : null;
        return (
          <div
            key={interview.id}
            className="flex items-center gap-1.5"
            data-testid={`badge-interview-status-${app.id}-${interview.id}`}
          >
            {multiple && (
              <span className="text-[10px] font-medium text-[hsl(var(--ink-muted))]">
                #{idx + 1}
              </span>
            )}
            {overdueOverride ? (
              <span className={`text-[12px] font-semibold ${overdueOverride}`}>{label}</span>
            ) : (
              <StatusBadge status={badgeKey} className="text-[12px]" />
            )}
            {rec === "advance" && (
              <span className="text-[12px] font-semibold text-[hsl(var(--forest-pulse))]">
                Recomenda
              </span>
            )}
            {rec === "reject" && (
              <span className="text-[12px] font-semibold text-[hsl(var(--ink-muted))]">
                Não recomenda
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const APPLICATION_TYPE_LABEL: Record<string, { label: string; className: string }> = {
  organic: {
    label: "Orgânico",
    className: "bg-[hsl(var(--amber-mist))] text-[hsl(var(--amber-state))]",
  },
  referral: {
    label: "Indicação",
    className: "bg-[hsl(var(--forest-mist))] text-[hsl(var(--forest-pulse))]",
  },
  hunting_comp: {
    label: "Hunting Comp",
    className: "bg-[hsl(var(--coral-mist))] text-[hsl(var(--comp-coral))]",
  },
  consultoria: {
    label: "Consultoria",
    className: "bg-[hsl(var(--violet-mist))] text-[hsl(var(--violet-pulse))]",
  },
};

function CardSourceBadge({ type }: { type: string | null | undefined }) {
  if (!type) return null;
  const entry = APPLICATION_TYPE_LABEL[type];
  if (!entry) return null;
  return (
    <span
      className={`mt-1 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none ${entry.className}`}
      data-testid={`badge-source-${type}`}
    >
      {entry.label}
    </span>
  );
}

function KanbanCard({
  app,
  column,
  data,
  draggingId,
  keyboardSelectedId,
  canDrag,
  onClick,
  onKeyDown,
  onDragStart,
  onDragEnd,
  renderPropertyValue,
}: {
  app: KanbanApplication;
  column: KanbanColumn;
  data: JobPipelineKanbanData;
  draggingId: string | null;
  keyboardSelectedId: string | null;
  canDrag: boolean;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onDragStart: ((e: React.DragEvent) => void) | undefined;
  onDragEnd: (() => void) | undefined;
  renderPropertyValue?: (input: { definitionId: string; applicationId: string }) => ReactNode;
}) {
  const alerts: string[] = [];
  if (!app.applicant?.email) alerts.push("Candidato sem email cadastrado");

  const filledCardProps = (data.propertyDefinitions ?? [])
    .filter((d) => d.showOnCard)
    .filter((def) => {
      const v = (app.propertyValues ?? []).find((pv) => pv.definitionId === def.id)?.value;
      return v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0) && v !== "";
    });

  return (
    // biome-ignore lint/a11y/useSemanticElements: card has nested interactive controls; cannot be a button
    <div
      key={app.id}
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      className={`bg-[hsl(var(--paper-white))] border border-[hsl(var(--sand-divider))] rounded-md p-3 ${
        canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } transition-shadow hover:shadow-[0_2px_6px_rgba(0,0,0,0.04)] ${
        keyboardSelectedId === app.id ? "ring-2 ring-[hsl(var(--comp-coral))]" : ""
      } ${draggingId === app.id ? "opacity-50" : ""}`}
      data-testid={`kanban-card-${app.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="font-medium text-sm truncate min-w-0" data-testid={`text-name-${app.id}`}>
            {app.applicant?.fullName || "Candidato"}
          </p>
          {alerts.length > 0 && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex-shrink-0 cursor-default"
                    data-testid={`icon-alert-${app.id}`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-[hsl(var(--amber-state))]" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <ul className="text-xs space-y-0.5">
                    {alerts.map((msg) => (
                      <li key={msg}>⚠️ {msg}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <CardAvatar app={app} column={column} data={data} />
      </div>

      <CardSourceBadge type={app.type} />

      <div className="flex items-center gap-2 mt-1.5">
        {(app.linkedinUrl || app.applicant?.linkedinUrl) && (
          <a
            href={ensureExternalUrl(app.linkedinUrl || app.applicant?.linkedinUrl || "")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--comp-coral))]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`link-linkedin-${app.id}`}
          >
            <Linkedin className="w-3.5 h-3.5" />
          </a>
        )}
        {app.applicant?.email && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (app.applicant?.email) {
                      navigator.clipboard.writeText(app.applicant.email);
                    }
                  }}
                  data-testid={`button-copy-email-${app.id}`}
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{app.applicant.email}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <CardScreening app={app} />

      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <CardColumnBadges
          app={app}
          column={column}
          notifyHmDailyDigest={data.notifyHmDailyDigest}
        />
      </div>

      {filledCardProps.length > 0 && renderPropertyValue && (
        <div className="mt-2 pt-2 border-t flex flex-wrap gap-1">
          {filledCardProps.map((def) =>
            renderPropertyValue({ definitionId: def.id, applicationId: app.id }),
          )}
        </div>
      )}
    </div>
  );
}

export function JobPipelineKanbanView({
  data,
  actions,
  hiddenColumnIds,
  searchQuery,
  keyboardSelectedId,
  canManage = false,
  scrollRef,
  renderPropertyValue,
}: {
  data: JobPipelineKanbanData;
  actions?: JobPipelineKanbanActions;
  hiddenColumnIds: Set<string>;
  searchQuery: string;
  keyboardSelectedId?: string | null;
  /** Unified ATS management capability (admin OR recruiting OR hiring manager). */
  canManage?: boolean;
  /** External ref for keyboard nav hook (auto-scroll on selection). Optional. */
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  renderPropertyValue?: (input: { definitionId: string; applicationId: string }) => ReactNode;
}) {
  const canMoveCards = Boolean(actions?.onMoveCard) && canManage;
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const finalScrollRef = (scrollRef ?? internalScrollRef) as React.RefObject<HTMLDivElement>;
  const scrollSpeedRef = useRef(0);
  const scrollAnimationRef = useRef<number | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hiddenColumnsExpanded, setHiddenColumnsExpanded] = useState(true);

  const startScrollLoop = useCallback(() => {
    if (scrollAnimationRef.current) return;
    const step = () => {
      const container = finalScrollRef.current;
      const speed = scrollSpeedRef.current;
      if (!container || speed === 0) {
        scrollAnimationRef.current = null;
        return;
      }
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (
        (speed < 0 && container.scrollLeft <= 0) ||
        (speed > 0 && container.scrollLeft >= maxScroll)
      ) {
        scrollAnimationRef.current = null;
        return;
      }
      container.scrollLeft += speed;
      scrollAnimationRef.current = requestAnimationFrame(step);
    };
    scrollAnimationRef.current = requestAnimationFrame(step);
  }, [finalScrollRef]);

  const stopScrollLoop = useCallback(() => {
    scrollSpeedRef.current = 0;
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopScrollLoop();
  }, [stopScrollLoop]);

  const handleBoardDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const container = finalScrollRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const distFromLeft = e.clientX - rect.left;
      const distFromRight = rect.right - e.clientX;
      let speed = 0;
      if (distFromLeft < EDGE_ZONE) {
        speed = -MAX_SCROLL_SPEED * (1 - distFromLeft / EDGE_ZONE);
      } else if (distFromRight < EDGE_ZONE) {
        speed = MAX_SCROLL_SPEED * (1 - distFromRight / EDGE_ZONE);
      }
      scrollSpeedRef.current = speed;
      if (speed !== 0) startScrollLoop();
    },
    [finalScrollRef, startScrollLoop],
  );

  const handleBoardDragLeave = useCallback(
    (e: React.DragEvent) => {
      const container = finalScrollRef.current;
      if (!container) return;
      if (!container.contains(e.relatedTarget as Node)) stopScrollLoop();
    },
    [finalScrollRef, stopScrollLoop],
  );

  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleColumnDrop = (e: React.DragEvent, column: KanbanColumn) => {
    e.preventDefault();
    if (!canMoveCards || !actions?.onMoveCard) return;
    const applicationId = e.dataTransfer.getData("applicationId");
    setDraggingId(null);
    if (!applicationId) return;
    const application =
      (data.stagesByColumn[column.id] ?? []).find((a) => a.id === applicationId) ??
      Object.values(data.stagesByColumn)
        .flat()
        .find((a) => a.id === applicationId);
    if (!application) return;
    void actions.onMoveCard({ applicationId, targetColumn: column, application });
  };

  const handleCardDragStart = (e: React.DragEvent, applicationId: string) => {
    e.dataTransfer.setData("applicationId", applicationId);
    setDraggingId(applicationId);
  };

  const handleCardDragEnd = () => {
    setDraggingId(null);
    stopScrollLoop();
  };

  const visibleColumns = data.columns.filter((column) => {
    if (hiddenColumnIds.has(column.id)) return false;
    if (searchQuery) {
      const apps = data.stagesByColumn[column.id] ?? [];
      return apps.length > 0;
    }
    return true;
  });

  const noMatches =
    searchQuery &&
    data.columns
      .filter((c) => !hiddenColumnIds.has(c.id))
      .every((col) => (data.stagesByColumn[col.id] ?? []).length === 0);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* biome-ignore lint/a11y/useSemanticElements: layout container for drag-and-drop */}
      <div
        ref={finalScrollRef}
        role="group"
        className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
        style={{ minHeight: "400px" }}
        onDragOver={handleBoardDragOver}
        onDragLeave={handleBoardDragLeave}
      >
        <div className="flex gap-4 min-w-max h-full pb-4">
          {visibleColumns.map((column) => {
            const columnApps = data.stagesByColumn[column.id] ?? [];
            const canDropHere = canMoveCards && !column.archived;
            return (
              // biome-ignore lint/a11y/useSemanticElements: drag-drop layout container
              <div
                key={column.id}
                role="group"
                className={`w-72 flex-shrink-0 flex flex-col h-full ${column.archived ? "opacity-60" : ""}`}
                onDragOver={canDropHere ? handleColumnDragOver : undefined}
                onDrop={canDropHere ? (e) => handleColumnDrop(e, column) : undefined}
                data-testid={`kanban-column-${column.id}`}
              >
                <div
                  className={`bg-[hsl(var(--paper-2))] border ${column.archived ? "border-dashed border-[hsl(var(--amber-state))]/40" : "border-[hsl(var(--sand-divider))]"} rounded-md p-3 flex flex-col h-full`}
                >
                  <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-semibold text-[12px] text-[hsl(var(--ink-primary))] truncate">
                        {column.title}
                      </h3>
                      {column.archived && (
                        <span className="text-[11px] font-semibold text-[hsl(var(--amber-state))]">
                          Arquivada
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] tabular-nums text-[hsl(var(--ink-muted))]">
                        {columnApps.length}
                      </span>
                      {actions?.onHideColumn && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="p-0.5 rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                              data-testid={`button-column-menu-${column.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onClick={() => actions.onHideColumn?.(column.id)}
                              data-testid={`button-hide-column-${column.id}`}
                            >
                              <EyeOff className="h-4 w-4 mr-2" />
                              Ocultar coluna
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 min-h-[80px] flex-1 overflow-y-auto pr-1">
                    {columnApps.map((app) => (
                      <KanbanCard
                        key={app.id}
                        app={app}
                        column={column}
                        data={data}
                        draggingId={draggingId}
                        keyboardSelectedId={keyboardSelectedId ?? null}
                        canDrag={canMoveCards && !column.archived}
                        onClick={() => actions?.onSelectCard?.(app.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            actions?.onSelectCard?.(app.id);
                          }
                        }}
                        onDragStart={(e) => handleCardDragStart(e, app.id)}
                        onDragEnd={handleCardDragEnd}
                        renderPropertyValue={renderPropertyValue}
                      />
                    ))}
                    {columnApps.length === 0 && (
                      <div className="flex items-center justify-center h-20 text-xs text-muted-foreground border-2 border-dashed rounded-md">
                        Arraste candidatos aqui
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {noMatches && (
            <div className="flex items-center justify-center w-full min-w-[300px] text-sm text-muted-foreground py-12">
              <div className="text-center">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nenhum candidato encontrado para "{searchQuery}"</p>
              </div>
            </div>
          )}
          {hiddenColumnIds.size > 0 && (
            <div
              className="w-64 flex-shrink-0 flex flex-col h-full"
              data-testid="hidden-columns-panel"
            >
              <div className="bg-muted/30 border border-dashed border-muted-foreground/20 rounded-lg p-3 flex flex-col">
                <button
                  type="button"
                  onClick={() => setHiddenColumnsExpanded((v) => !v)}
                  className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
                  data-testid="button-toggle-hidden-columns"
                >
                  {hiddenColumnsExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  Colunas ocultas
                  <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full ml-1">
                    {hiddenColumnIds.size}
                  </span>
                </button>
                {hiddenColumnsExpanded && (
                  <div className="space-y-1">
                    {data.columns
                      .filter((col) => hiddenColumnIds.has(col.id))
                      .map((col) => {
                        const apps = data.stagesByColumn[col.id] ?? [];
                        return (
                          <div
                            key={col.id}
                            className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-background border text-sm"
                            data-testid={`hidden-column-item-${col.id}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate">{col.title}</span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {apps.length}
                              </span>
                            </div>
                            {actions?.onShowColumn && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                                    data-testid={`button-hidden-column-menu-${col.id}`}
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                  <DropdownMenuItem
                                    onClick={() => actions.onShowColumn?.(col.id)}
                                    data-testid={`button-show-column-${col.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Mostrar coluna
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Suppress unused — STATUS_LABELS is exported but only consumed by container.
void STATUS_LABELS;
